import s from './Step1.module.scss';
import { Input } from '@/shared/ui/Input/Input';
import { Button, ButtonMobile } from '@/shared/ui/Button';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Step1Data } from '../../model/types/step1';
import { step1Schema } from '../../lib/validationSchema/Step1Schema';
import { useEffect, useState } from 'react';
import { useResendCodeMutation, useStep1Mutation } from '../../api/registrationApi';
import { useAppDispatch, useAppSelector } from '@/shared/lib/hooks/redux';
import {
  setRegistrationToken,
  setResendCooldown,
  setLastResendChannel,
  setRemainingAttempts,
  setLockedUntil,
  setLockoutMessage,
  saveRegistrationSession,
} from '@/features/RegistrationForm';
import { nextStep, prevStep } from '@/widgets/RegistrationStepper';
import { useRemainingTimer } from '@/shared/lib/hooks/useRemainingTimer';
import { useScreenDetector } from '@/shared/lib/hooks/useScreenDetector';
import { useAlert } from '@/shared/lib/hooks/useAlert';
import { normalizeApiError } from '@/shared/lib/utils/apiError';
import { extractOtpDetails } from '@/shared/lib/utils/extractOtpDetails';
import { getOtpAlertText } from '@/shared/lib/utils/getOtpAlertText';
import { Alert } from '@/shared/ui/Alert/Alert';
import { linkifyEmail } from '@/shared/lib/utils/linkifyEmail';
import { phoneNumberFormat } from '@/shared/lib/utils/phoneNumberFormat';

export const Step1 = () => {
  const dispatch = useAppDispatch();
  const [triggerStep1, { isLoading, isSuccess, error, data }] = useStep1Mutation();
  const [triggerResend, { isLoading: isResendLoading }] = useResendCodeMutation();
  const { isMobile } = useScreenDetector();
  const [codeExpiredAlert, setCodeExpiredAlert] = useState<string | null>(null);
  const onAlert = useAlert();

  const {
    registrationToken,
    maxDeepLink,
    sendChannel,
    cooldowns,
    remainingAttempts,
    lockedUntil,
    lockoutMessage,
    data: { phoneNumber },
  } = useAppSelector((state) => state.registration);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<Step1Data>({
    resolver: yupResolver(step1Schema),
    defaultValues: { code: '' },
  });

  const channelCooldown = cooldowns[sendChannel ?? 'max'];
  const timeRemaining = useRemainingTimer(channelCooldown.seconds, channelCooldown.timestamp ?? 0);
  const lockedSecondsRemaining = useRemainingTimer(lockedUntil ?? 0, 0);
  const isLocked = lockedSecondsRemaining > 0;

  useEffect(() => {
    if (!isLocked && lockedUntil !== null) {
      dispatch(setLockedUntil(null));
      dispatch(setLockoutMessage(null));
      setCodeExpiredAlert(null);
      dispatch(saveRegistrationSession(phoneNumberFormat(phoneNumber)));
    }
  }, [isLocked, lockedUntil, dispatch, phoneNumber]);

  useEffect(() => {
    if (!error) {
      setCodeExpiredAlert(null);
      return;
    }
    const normalized = normalizeApiError(error);
    if (!normalized) {
      onAlert('Произошла неизвестная ошибка', true);
      return;
    }

    if (normalized?.code !== 'OTP_CODE_EXPIRED') {
      setCodeExpiredAlert(null);
    }
    if (normalized?.code === 'OTP_CODE_EXPIRED') {
      setCodeExpiredAlert(normalized.message);
      return;
    }

    const { remainingAttempts: attempts, lockedUntil: lu } = extractOtpDetails(normalized);
    if (typeof attempts === 'number') {
      dispatch(setRemainingAttempts(attempts));
    }
    if (typeof lu === 'number') {
      dispatch(setLockedUntil(lu));
    }
    if (typeof attempts === 'number' || typeof lu === 'number') {
      dispatch(setLockoutMessage(normalized.code === 'OTP_ATTEMPTS_EXHAUSTED' ? normalized.message : null));
      dispatch(saveRegistrationSession(phoneNumberFormat(phoneNumber)));
      return;
    }
    onAlert(linkifyEmail(normalized.message), true);
  }, [error, dispatch]);

  const alertText = lockoutMessage || codeExpiredAlert || getOtpAlertText(remainingAttempts, lockedUntil);

  const onSubmit: SubmitHandler<Step1Data> = (data) => {
    if (!registrationToken) return;
    if (isLocked || remainingAttempts === 0) return;
    triggerStep1({
      registrationToken,
      code: data.code,
    });
  };

  const handleResend = async () => {
    if (timeRemaining || !registrationToken) return;
    const channel = sendChannel === 'sms' ? 'sms' : 'max';
    try {
      const res = await triggerResend({
        registrationToken,
        channel,
      }).unwrap();
      const retryAfterSeconds = typeof res?.retryAfterSeconds === 'number' ? res.retryAfterSeconds : 60;
      dispatch(
        setResendCooldown({
          channel,
          timestamp: Date.now(),
          seconds: retryAfterSeconds,
        }),
      );
      dispatch(setLastResendChannel(channel));
      dispatch(setRemainingAttempts(res.remainingAttempts ?? 5));
      dispatch(setLockedUntil(null));
      dispatch(setLockoutMessage(null));
      dispatch(saveRegistrationSession(phoneNumberFormat(phoneNumber)));
    } catch (error) {
      const normalized = normalizeApiError(error);
      const { lockedUntil: lu, retryAfterSeconds } = extractOtpDetails(normalized);
      const isAttemptsExhausted = normalized?.code === 'OTP_ATTEMPTS_EXHAUSTED';
      if (typeof lu === 'number') {
        dispatch(setLockedUntil(lu));
      }
      if (isAttemptsExhausted) {
        dispatch(setRemainingAttempts(0));
        dispatch(setLockoutMessage(normalized.message));
      }
      if (typeof retryAfterSeconds === 'number') {
        dispatch(
          setResendCooldown({
            channel,
            timestamp: Date.now(),
            seconds: retryAfterSeconds,
          }),
        );
      }
      if (typeof lu === 'number' || typeof retryAfterSeconds === 'number' || isAttemptsExhausted) {
        dispatch(saveRegistrationSession(phoneNumberFormat(phoneNumber)));
      }
      if (!isAttemptsExhausted) {
        const message = normalized?.message || 'Не удалось отправить код. Попробуйте позже.';
        onAlert(linkifyEmail(message), true);
      }
    }
  };

  useEffect(() => {
    if (isSuccess && data) {
      dispatch(setRegistrationToken(data.registrationToken));
      dispatch(setLockoutMessage(null));
      dispatch(saveRegistrationSession(phoneNumberFormat(phoneNumber)));
      dispatch(nextStep());
    }
  }, [isSuccess, data, dispatch]);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const inputDisabled = !registrationToken || isLoading || isLocked || remainingAttempts === 0;

  if (sendChannel === 'max') {
    return (
      <div className={s.wrapper}>
        <div className={s.headerGroup}>
          <h2 className={s.title}>Получите код в Макс</h2>
          <p className={s.description}>Перейдите в наш бот и поделитесь контактом, чтобы получить код подтверждения</p>
        </div>

        {isMobile ? (
          <ButtonMobile
            onClick={() => {
              if (maxDeepLink) {
                window.open(maxDeepLink, '_blank');
              }
            }}
          >
            Перейти в Макс
          </ButtonMobile>
        ) : (
          <Button textSize={'b2'} href={maxDeepLink ?? undefined}>
            Перейти в Макс
          </Button>
        )}

        {alertText && <Alert className={s.alert}>{alertText}</Alert>}

        <form className={s.form} onSubmit={handleSubmit(onSubmit)} autoComplete={'off'}>
          <Controller
            name="code"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                onChange={(e) => {
                  const value = typeof e === 'string' ? e : e.target.value;
                  field.onChange(value);
                  setCodeExpiredAlert(null);
                  if (value.length === 4 && !value.includes('_') && !isLocked) {
                    handleSubmit(onSubmit)();
                  }
                }}
                placeholder={'Код подтверждения'}
                error={errors.code?.message}
                disabled={inputDisabled}
                baseAddon={false}
                mask={'0000'}
              />
            )}
          />
        </form>

        {isMobile ? (
          <>
            <ButtonMobile
              variant={'ghost'}
              color={'green'}
              disabled={!!timeRemaining || isResendLoading || isLocked}
              onClick={handleResend}
            >
              Отправить код повторно
              {isLocked
                ? ` ${formatTime(lockedSecondsRemaining)}`
                : timeRemaining > 0
                  ? ` ${formatTime(timeRemaining)}`
                  : ''}
            </ButtonMobile>
            {isLocked && (
              <ButtonMobile variant={'ghost'} color={'green'} onClick={() => dispatch(prevStep())}>
                Изменить номер
              </ButtonMobile>
            )}
          </>
        ) : (
          <>
            <Button
              textSize={'b2'}
              variant={'ghost'}
              height={'48px'}
              disabled={!!timeRemaining || isResendLoading || isLocked}
              onClick={handleResend}
            >
              Отправить код повторно
              {isLocked
                ? ` ${formatTime(lockedSecondsRemaining)}`
                : timeRemaining > 0
                  ? ` ${formatTime(timeRemaining)}`
                  : ''}
            </Button>
            <Button
              textSize={'b2'}
              variant={'outline'}
              width={'auto'}
              className={s.backButton}
              size={'m'}
              onClick={() => dispatch(prevStep())}
            >
              Назад
            </Button>
          </>
        )}
      </div>
    );
  }

  return (
    <div className={s.wrapper}>
      <h2 className={s.title}>Мы отправили код по СМС</h2>

      {alertText && <Alert className={s.alert}>{alertText}</Alert>}

      <form className={s.form} onSubmit={handleSubmit(onSubmit)} autoComplete={'off'}>
        <Controller
          name="code"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              onChange={(e) => {
                const value = typeof e === 'string' ? e : e.target.value;
                field.onChange(value);
                setCodeExpiredAlert(null);
                if (value.length === 4 && !value.includes('_') && !isLocked) {
                  handleSubmit(onSubmit)();
                }
              }}
              placeholder={'Код подтверждения'}
              error={errors.code?.message}
              disabled={inputDisabled}
              baseAddon={false}
              mask={'0000'}
            />
          )}
        />
      </form>

      {isMobile ? (
        <>
          <ButtonMobile
            variant={'ghost'}
            color={'green'}
            disabled={!!timeRemaining || isResendLoading || isLocked}
            onClick={handleResend}
          >
            Отправить код повторно
            {isLocked
              ? ` ${formatTime(lockedSecondsRemaining)}`
              : timeRemaining > 0
                ? ` ${formatTime(timeRemaining)}`
                : ''}
          </ButtonMobile>
          {isLocked && (
            <ButtonMobile variant={'ghost'} color={'green'} onClick={() => dispatch(prevStep())}>
              Изменить номер
            </ButtonMobile>
          )}
        </>
      ) : (
        <>
          <Button
            textSize={'b2'}
            variant={'ghost'}
            height={'48px'}
            disabled={!!timeRemaining || isResendLoading || isLocked}
            onClick={handleResend}
          >
            Отправить код повторно
            {isLocked
              ? ` ${formatTime(lockedSecondsRemaining)}`
              : timeRemaining > 0
                ? ` ${formatTime(timeRemaining)}`
                : ''}
          </Button>
          <Button
            textSize={'b2'}
            variant={'outline'}
            width={'auto'}
            className={s.backButton}
            size={'m'}
            onClick={() => dispatch(prevStep())}
          >
            Назад
          </Button>
        </>
      )}
    </div>
  );
};
